import { useMemo, useState, useEffect, useCallback } from 'react';
import { DocumentType } from 'src/shared/types';
import { useDocumentTypes } from 'src/hooks/useDocumentTypes';
import Loading from 'src/components/Loading';
import { StaticDataTable, StaticDataColumnProps, StaticDataRow } from 'src/components/table/StaticDataTable';
import { SortDirection } from 'src/components/table/useTableData';
import TableDateTime from 'src/components/table/TableDateTime';
import { InformationCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import Input from 'src/components/inputs/Input';
import SubmitButton from 'src/components/SubmitButton';
import ActionsMenu from 'src/components/ActionsMenu';

const CLINICAL_NOTE_COLUMNS: StaticDataColumnProps[] = [
  { title: 'TXA-2 ID', sortable: true },
  { title: 'Label', sortable: true, defaultSort: true, defaultSortDirection: SortDirection.ASC },
  { title: 'Created At', sortable: true },
];

const docTypeToRow = (docType: DocumentType, onDelete: (id: string) => void): StaticDataRow[] => {
  const d = new Date(docType.created_at);
  const datePart = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return [
    {
      name: 'TXA-2 ID',
      value: docType.txa_id,
      element: <span className="text-sm font-mono font-medium text-gray-900">{docType.txa_id}</span>,
    },
    {
      name: 'Label',
      value: docType.label,
      element: <span className="text-sm text-gray-900">{docType.label}</span>,
    },
    {
      name: 'Created At',
      value: d.getTime(),
      element: <TableDateTime date={datePart} startTime={time} />,
    },
    {
      name: 'Actions',
      value: '',
      element: (
        <ActionsMenu
          actions={[
            { id: 'delete', label: 'Delete', onClick: () => onDelete(docType.id) },
          ]}
        />
      ),
    },
  ];
};

const ClinicalNoteTypesSection = () => {
  const { fetchDocumentTypes, addDocumentType, deleteDocumentType, error: documentTypeError } = useDocumentTypes('clinical-note');
  const [items, setItems] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTxaId, setNewTxaId] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const loadTypes = useCallback(async () => {
    setLoading(true);
    const res = await fetchDocumentTypes();
    setItems(res);
    setLoading(false);
  }, [fetchDocumentTypes]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const handleDeleteClinicalNoteType = useCallback(async (id: string) => {
    await deleteDocumentType(id);
    loadTypes();
  }, [deleteDocumentType, loadTypes]);

  const rows = useMemo(() => {
    return items.map(docType => docTypeToRow(docType, handleDeleteClinicalNoteType));
  }, [items, handleDeleteClinicalNoteType]);

  const handleAddClinicalNoteType = async () => {
    if (!newTxaId.trim() || !newLabel.trim()) return;
    await addDocumentType(newTxaId.trim(), newLabel.trim());
    setNewTxaId('');
    setNewLabel('');
    loadTypes();
  };

  if (loading) {
    return <Loading message="Loading clinical note types..." />;
  }

  return (
    <>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Clinical Note Type Mappings</h3>
      <div className="space-y-4">
        <div className="mt-2 flex gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-700" aria-hidden />
          <p className="text-sm text-blue-700">
            Clinical note types must match the values configured in your EHR instance.
            The label is the display name shown to users when uploading.
          </p>
        </div>

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">TXA-2 ID</label>
            <Input
              type="text"
              label=""
              placeholder="e.g. PN"
              value={newTxaId}
              onChange={(e) => setNewTxaId(e.target.value)}
              required={false}
              inputClassName="w-full"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
            <Input
              type="text"
              label=""
              placeholder="e.g. Progress Note"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              required={false}
              inputClassName="w-full"
            />
          </div>
          <SubmitButton
            loading={false}
            text="Add"
            loadingText="Adding..."
            onClick={handleAddClinicalNoteType}
            className="w-1/8"
            icon={<PlusIcon className="w-4 h-4 stroke-2" />}
          />
        </div>
        {documentTypeError && <p className="text-red-500 mt-2">{documentTypeError}</p>}

        <StaticDataTable
          tableId="clinical-note-types"
          columns={CLINICAL_NOTE_COLUMNS}
          rows={rows}
          updateUrlParams={false}
          headerClass="bg-gray-50"
          headerColor={false}
          trailingColumnKey="Actions"
          rowId={(item: Record<string, any>) => String(item['TXA-2 ID'])}
        />
      </div>
    </>
  );
};

export default ClinicalNoteTypesSection;
